import React from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineMap, HiOutlineClipboardDocumentCheck, HiOutlineChartBarSquare, HiOutlineArrowRight } from 'react-icons/hi2';
import { TbDatabase, TbRobot, TbShieldLock } from 'react-icons/tb';

const SolutionsPage = () => {
  const solutions = [
    {
      icon: <HiOutlineMap />,
      title: 'AI Roadmap & Strategy',
      desc: 'Define a clear AI vision aligned with business goals. We identify cost drivers, assess organizational readiness, and create a phased roadmap for AI adoption that maximizes ROI.',
      features: ['Executive AI Workshops', 'Maturity Assessment', 'Use Case Prioritization', 'ROI Modeling'],
    },
    {
      icon: <TbDatabase />,
      title: 'Data Platform Modernization',
      desc: 'Build a scalable, cost-effective data foundation. We centralize your data lakes, optimize pipelines, and implement modern architectures that power AI workloads.',
      features: ['Data Lake Architecture', 'Pipeline Optimization', 'Real-time Processing', 'Data Quality Frameworks'],
    },
    {
      icon: <TbRobot />,
      title: 'GenAI / RAG Applications',
      desc: 'Deploy production-ready GenAI solutions tailored to your enterprise. From retrieval-augmented generation to custom copilots, we build AI that understands your domain.',
      features: ['RAG Pipelines', 'Custom Copilots', 'Document Intelligence', 'Conversational AI'],
    },
    {
      icon: <HiOutlineClipboardDocumentCheck />,
      title: 'LLM Evaluation & Testing',
      desc: 'Benchmark and evaluate LLMs for accuracy, hallucination, bias, and cost. Our evaluation frameworks ensure you deploy models that meet enterprise standards.',
      features: ['Benchmark Suites', 'Hallucination Detection', 'Bias Analysis', 'Cost Optimization'],
    },
    {
      icon: <TbShieldLock />,
      title: 'MLOps & Governance',
      desc: 'Operationalize ML with robust governance. We implement model registries, automated retraining, drift detection, and compliance frameworks for responsible AI.',
      features: ['Model Registry', 'Automated Retraining', 'Drift Detection', 'Compliance Frameworks'],
    },
    {
      icon: <HiOutlineChartBarSquare />,
      title: 'AI Analytics & Observability',
      desc: 'Monitor AI systems in production with comprehensive observability. Track model performance, user interactions, and business impact in real-time.',
      features: ['Performance Dashboards', 'Usage Analytics', 'Alerting Systems', 'Business Impact Tracking'],
    },
  ];

  return (
    <>
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="hero-text">
              <div className="section-label">What We Offer</div>
              <h1>Our Solutions</h1>
              <p>End-to-end AI enablement solutions designed to take you from strategy to production with confidence.</p>
            </div>
          </div>
        </div>
        <div className="hero-image-wrapper">
          <img
            src="/images/solutions-hero.png"
            alt="Enterprise AI Solutions & Global Data Processing"
          />
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="services-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
            {solutions.map((sol, i) => (
              <div className="service-card" key={i} style={{ padding: '40px 32px' }}>
                <div className="service-icon" style={{ width: 64, height: 64, fontSize: 30 }}>{sol.icon}</div>
                <h3 style={{ fontSize: 22, marginBottom: 14 }}>{sol.title}</h3>
                <p style={{ marginBottom: 20 }}>{sol.desc}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  {sol.features.map((feat, j) => (
                    <span key={j} style={{
                      background: 'rgba(34,211,238,0.08)', color: 'var(--cyan)',
                      padding: '4px 12px', borderRadius: '50px', fontSize: 12, fontWeight: 600,
                      border: '1px solid rgba(34,211,238,0.2)',
                    }}>
                      {feat}
                    </span>
                  ))}
                </div>
                <Link to="/contact" className="learn-more">Get Started <HiOutlineArrowRight /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="architecture-section">
            <div className="arch-image">
              <img src="/images/enterprise-architecture.png" alt="Enterprise Architecture" />
            </div>
            <div>
              <div className="section-label">Our Approach</div>
              <h2 style={{ fontSize: 32, fontWeight: 700, marginBottom: 16 }}>
                Enterprise AI Enablement Architecture
              </h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 32, lineHeight: 1.8 }}>
                Our proven architecture enables secure, scalable AI deployment with built-in governance and observability.
              </p>
              <ul className="arch-features">
                {['Secure Data Ingestion', 'Knowledge Grounding', 'Vector Search & Retrieval', 'Continuous Evaluation', 'Observability & Monitoring', 'CI/CD Integration'].map((f) => (
                  <li key={f}><span className="arch-bullet"></span>{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to build production-grade AI?</h2>
            <p>Let's architect a solution tailored to your enterprise needs.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Book a Discovery Call <HiOutlineArrowRight /></Link>
              <Link to="/case-studies" className="btn-secondary">View Case Studies</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SolutionsPage;
